import { useState, useEffect, useContext, useMemo } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { 
  Package, Search, Filter, Plus, AlertTriangle, CheckCircle, TrendingDown,
  TrendingUp, RefreshCw, Calendar, BarChart3, Layers, ChevronLeft, ChevronRight
} from 'lucide-react';

// Dynamic month generator
function generateMonths() {
  const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const SHORT_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const months = [];
  let year = 2025, month = 10;
  const now = new Date();
  const curYear = now.getFullYear(), curMonth = now.getMonth() + 1;
  while (year < curYear || (year === curYear && month <= curMonth)) {
    months.push({
      key: `used${MONTH_NAMES[month-1]}${year}`,
      label: `${SHORT_NAMES[month-1]} ${year}`,
      short: SHORT_NAMES[month-1],
      year, month
    });
    month++;
    if (month > 12) { month = 1; year++; }
  }
  return months;
}

const MONTHS = generateMonths();

const RECORDS_PER_PAGE = 200;

function RawMaterialStock() {
  const { API } = useContext(AppContext);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterMaterial, setFilterMaterial] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [filterMonth, setFilterMonth] = useState('all');
  const [viewMode, setViewMode] = useState('detail');
  const [currentPage, setCurrentPage] = useState(1);
  const [monthlySummary, setMonthlySummary] = useState([]);

  useEffect(() => {
    fetchData();
    fetchMonthlySummary();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/master-data/raw-material/consumption_stock`);
      setRecords(response.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      setRecords([]);
    }
    setLoading(false);
  };

  const fetchMonthlySummary = async () => {
    try {
      const response = await axios.get(`${API}/raw-material/consumption-stock/monthly-summary`);
      setMonthlySummary(response.data.summaries || []);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // Get unique values for filters
  const materialTypes = useMemo(() => {
    const types = new Set(records.map(r => r.materialType).filter(Boolean));
    return [...types].sort();
  }, [records]);

  const suppliers = useMemo(() => {
    const sups = new Set(records.map(r => r.supplierName).filter(Boolean));
    return [...sups].sort();
  }, [records]);

  // Get unique receiving months for filter
  const receivingMonths = useMemo(() => {
    const monthSet = new Map();
    records.forEach(r => {
      const d = r.receivingDate;
      if (!d) return;
      const date = new Date(d);
      if (isNaN(date)) return;
      const y = date.getFullYear();
      const m = date.getMonth(); // 0-indexed
      const key = `${y}-${String(m + 1).padStart(2, '0')}`;
      const SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      monthSet.set(key, `${SHORT[m]} ${y}`);
    });
    return [...monthSet.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [records]);

  // Filter records
  const filteredRecords = useMemo(() => {
    return records.filter(r => {
      const matchesSearch = !searchTerm || 
        (r.supplierName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.invoiceNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.materialType || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesMaterial = filterMaterial === 'all' || r.materialType === filterMaterial;
      const matchesSupplier = filterSupplier === 'all' || r.supplierName === filterSupplier;
      
      let matchesMonth = true;
      if (filterMonth !== 'all' && r.receivingDate) {
        const date = new Date(r.receivingDate);
        if (!isNaN(date)) {
          const rMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          matchesMonth = rMonth === filterMonth;
        }
      }
      
      return matchesSearch && matchesMaterial && matchesSupplier && matchesMonth;
    });
  }, [records, searchTerm, filterMaterial, filterSupplier, filterMonth]);

  // Pagination
  const totalPages = Math.ceil(filteredRecords.length / RECORDS_PER_PAGE);
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * RECORDS_PER_PAGE;
    return filteredRecords.slice(startIndex, startIndex + RECORDS_PER_PAGE);
  }, [filteredRecords, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterMaterial, filterSupplier, filterMonth]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    const stats = {
      totalReceived: 0,
      totalConsumed: 0,
      totalBalance: 0,
      byMaterial: {},
      monthlyConsumption: Object.fromEntries(MONTHS.map(m => [m.key, 0])),
    };

    filteredRecords.forEach(r => {
      const received = parseFloat(r.receivingQuantity) || 0;
      const balance = parseFloat(r.balanceStock) || 0;
      
      stats.totalReceived += received;
      stats.totalBalance += balance;

      // Monthly consumption
      MONTHS.forEach(m => {
        const val = parseFloat(r[m.key]) || 0;
        stats.monthlyConsumption[m.key] += val;
        stats.totalConsumed += val;
      });

      // By material type
      if (r.materialType) {
        if (!stats.byMaterial[r.materialType]) {
          stats.byMaterial[r.materialType] = { received: 0, consumed: 0, balance: 0 };
        }
        stats.byMaterial[r.materialType].received += received;
        stats.byMaterial[r.materialType].balance += balance;
        MONTHS.forEach(m => {
          stats.byMaterial[r.materialType].consumed += parseFloat(r[m.key]) || 0;
        });
      }
    });

    return stats;
  }, [filteredRecords]);

  // Material summary for summary view
  const materialSummary = useMemo(() => {
    return Object.entries(summaryStats.byMaterial).map(([material, data]) => ({
      material,
      ...data,
    })).sort((a, b) => b.received - a.received);
  }, [summaryStats]);

  const formatNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) return '-';
    const mtValue = parseFloat(num) / 1000;
    if (isNaN(mtValue)) return '-';
    return mtValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="space-y-6" data-testid="aluminium-scrap-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
            <Layers className="text-white" size={24} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Aluminium Scrap Stock</h1>
            <p className="text-sm text-gray-500">{records.length} records • Track receipts, consumption & balance</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <TrendingUp className="text-blue-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Received</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summaryStats.totalReceived)} <span className="text-sm font-normal text-gray-500">MT</span></p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <TrendingDown className="text-orange-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Consumed</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summaryStats.totalConsumed)} <span className="text-sm font-normal text-gray-500">MT</span></p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-100 rounded-lg">
              <Package className="text-emerald-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Balance Stock</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(summaryStats.totalBalance)} <span className="text-sm font-normal text-gray-500">MT</span></p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <BarChart3 className="text-purple-600" size={20} />
            </div>
            <div>
              <p className="text-sm text-gray-500">Material Types</p>
              <p className="text-2xl font-bold text-gray-900">{materialTypes.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Monthly Consumption Bar */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Calendar size={18} className="text-blue-600" />
          Monthly Consumption ({MONTHS[0]?.label} - {MONTHS[MONTHS.length-1]?.label})
        </h3>
        <div className={`grid gap-3`} style={{gridTemplateColumns: `repeat(${Math.min(MONTHS.length, 7)}, minmax(0, 1fr))`}}>
          {MONTHS.map(month => (
            <div key={month.key} className="text-center">
              <div className="bg-gradient-to-b from-blue-50 to-blue-100 rounded-lg p-3 border border-blue-200">
                <p className="text-xs text-gray-500 mb-1">{month.short}</p>
                <p className="text-lg font-bold text-blue-700">{formatNumber(summaryStats.monthlyConsumption[month.key])}</p>
                <p className="text-xs text-gray-400">MT</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search supplier, invoice, material..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
              data-testid="stock-search"
            />
          </div>
          <select
            value={filterMaterial}
            onChange={(e) => setFilterMaterial(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100"
          >
            <option value="all">All Materials</option>
            {materialTypes.map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
          <select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100 max-w-xs"
          >
            <option value="all">All Suppliers</option>
            {suppliers.map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <select
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-100"
            data-testid="stock-month-filter"
          >
            <option value="all">All Months</option>
            {receivingMonths.map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('detail')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'detail' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
              }`}
            >
              Detail View
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition ${
                viewMode === 'summary' ? 'bg-white shadow text-blue-600' : 'text-gray-600'
              }`}
            >
              Summary
            </button>
          </div>
        </div>

        {/* Material Type Filter Pills */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterMaterial('all')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              filterMaterial === 'all' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Materials
          </button>
          {materialTypes.map(m => (
            <button
              key={m}
              onClick={() => setFilterMaterial(m)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                filterMaterial === m 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Last 6 Months Summary Cards */}
      {monthlySummary.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="monthly-summary-cards">
          {monthlySummary.map((m, i) => {
            const isCurrentMonth = i === monthlySummary.length - 1;
            return (
              <div key={m.key} className={`rounded-xl border p-3 transition ${
                isCurrentMonth 
                  ? 'bg-blue-50 border-blue-300 ring-2 ring-blue-200' 
                  : 'bg-white border-gray-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-xs font-bold ${isCurrentMonth ? 'text-blue-700' : 'text-gray-500'}`}>
                    {m.label}
                  </span>
                  {isCurrentMonth && (
                    <span className="text-[9px] bg-blue-600 text-white px-1.5 py-0.5 rounded-full font-bold">CURRENT</span>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Received</span>
                    <span className="font-semibold text-blue-600">{m.received?.toLocaleString() || 0}</span>
                  </div>
                  <div className="flex justify-between text-[11px]">
                    <span className="text-gray-500">Consumed</span>
                    <span className="font-semibold text-red-500">{m.consumed?.toLocaleString() || 0}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-1 flex justify-between text-[11px]">
                    <span className="text-gray-500">Closing</span>
                    <span className={`font-bold ${m.closingBalance > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {m.closingBalance?.toLocaleString() || 0}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary View */}
      {viewMode === 'summary' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="font-semibold text-gray-800">Material Type Summary</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Material Type</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total Received (MT)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Total Consumed (MT)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Balance Stock (MT)</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Utilization %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {materialSummary.map(item => (
                  <tr key={item.material} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <span className="font-medium text-gray-900">{item.material}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatNumber(item.received)}</td>
                    <td className="px-4 py-3 text-right text-gray-900">{formatNumber(item.consumed)}</td>
                    <td className="px-4 py-3 text-right font-medium text-blue-600">{formatNumber(item.balance)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.received > 0 && (item.consumed / item.received * 100) > 90 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.received > 0 ? ((item.consumed / item.received) * 100).toFixed(1) : 0}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-blue-50 border-t-2 border-blue-200">
                <tr>
                  <td className="px-4 py-3 font-bold text-gray-800">TOTAL</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{formatNumber(summaryStats.totalReceived)}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-800">{formatNumber(summaryStats.totalConsumed)}</td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700">{formatNumber(summaryStats.totalBalance)}</td>
                  <td className="px-4 py-3 text-right font-bold text-blue-700">
                    {summaryStats.totalReceived > 0 
                      ? ((summaryStats.totalConsumed / summaryStats.totalReceived) * 100).toFixed(1) 
                      : 0}%
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Detail View */}
      {viewMode === 'detail' && (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
            <h3 className="font-semibold text-gray-800">Receipt Details ({filteredRecords.length} records)</h3>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw size={24} className="animate-spin mx-auto mb-2 text-gray-400" />
              <p className="text-gray-500">Loading records...</p>
            </div>
          ) : filteredRecords.length === 0 ? (
            <div className="p-8 text-center">
              <Package size={48} className="mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">No records found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">#</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Supplier</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Invoice</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Material</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600">Received</th>
                    {MONTHS.map(m => (
                      <th key={m.key} className="px-3 py-3 text-right text-xs font-semibold text-gray-600 bg-orange-50">{m.short}</th>
                    ))}
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600 bg-blue-50">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {paginatedRecords.map((r, idx) => (
                    <tr key={r.id || idx} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-gray-500">{(currentPage - 1) * RECORDS_PER_PAGE + idx + 1}</td>
                      <td className="px-3 py-2 font-medium text-gray-900 max-w-[200px] truncate" title={r.supplierName}>
                        {r.supplierName || '-'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDate(r.receivingDate)}</td>
                      <td className="px-3 py-2 text-gray-600">{r.invoiceNumber || '-'}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                          {r.materialType || '-'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-medium text-gray-900">{formatNumber(r.receivingQuantity)}</td>
                      {MONTHS.map(m => (
                        <td key={m.key} className="px-3 py-2 text-right bg-orange-50/50">{formatNumber(r[m.key])}</td>
                      ))}
                      <td className="px-3 py-2 text-right bg-blue-50/50 font-medium text-blue-700">{formatNumber(r.balanceStock)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination Controls */}
              {filteredRecords.length > RECORDS_PER_PAGE && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    Showing {(currentPage - 1) * RECORDS_PER_PAGE + 1} - {Math.min(currentPage * RECORDS_PER_PAGE, filteredRecords.length)} of {filteredRecords.length} records
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      <ChevronLeft size={16} />
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 rounded-lg">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
                    >
                      Next
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RawMaterialStock;
